import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Shield, Activity, Search, RefreshCw, Filter,
  Users, Building2, CreditCard, Clock, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { superadminApi } from '../lib/api';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import { format, formatDistanceToNow } from 'date-fns';

const ActivityLogPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('');
  const limit = 50;

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    fetchLogs();
  // Runs once on mount. `fetchLogs` is recreated every render, so listing it would re-fetch endlessly. `navigate` is stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, user]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await superadminApi.getActivityLog({
        limit,
        offset: page * limit,
        action_filter: filter || undefined
      });
      setLogs(res.data.items);
      setTotal(res.data.total);
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
      toast.error('Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const getActionIcon = (action) => {
    if (action.includes('tenant') || action.includes('customer')) {
      return <Building2 className="w-4 h-4" />;
    }
    if (action.includes('user')) {
      return <Users className="w-4 h-4" />;
    }
    if (action.includes('trial') || action.includes('paid')) {
      return <CreditCard className="w-4 h-4" />;
    }
    return <Activity className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('created') || action.includes('activated') || action.includes('converted')) {
      return 'text-green-500 bg-green-500/10';
    }
    if (action.includes('suspended') || action.includes('deactivated') || action.includes('deleted')) {
      return 'text-red-500 bg-red-500/10';
    }
    if (action.includes('extended') || action.includes('updated')) {
      return 'text-yellow-500 bg-yellow-500/10';
    }
    return 'text-blue-500 bg-blue-500/10';
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandedToaster />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-card border-b border-red-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/superadmin')}
                className="text-muted-foreground"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Activity className="w-6 h-6 text-red-500" />
                <h1 className="font-heading text-xl font-bold">Activity Log</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/superadmin" className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                <span className="font-heading font-bold text-sm text-red-500">ADMIN</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Search and Filter */}
        <div className="card-hive p-4 mb-6 border-red-500/20">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by action type (e.g. trial, tenant, user)..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
              <Button onClick={handleSearch} variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
            </div>
            <Button onClick={fetchLogs} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground">
            Showing {logs.length} of {total} activities
          </p>
        </div>

        {/* Activity List */}
        <div className="card-hive border-red-500/20 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No activity recorded yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {logs.map((log) => (
                <div key={log.id} className="p-4 hover:bg-secondary/30 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getActionColor(log.action)}`}>
                      {getActionIcon(log.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-medium">{log.description}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <span className={`px-2 py-0.5 rounded text-xs ${getActionColor(log.action)}`}>
                              {log.action.replace(/_/g, ' ')}
                            </span>
                            {log.entity_name && (
                              <span>• {log.entity_name}</span>
                            )}
                            {log.actor_email && (
                              <span>• by {log.actor_email}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                          <p>{formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}</p>
                          <p className="text-xs">{format(new Date(log.created_at), 'PPp')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-sm text-muted-foreground">
              Page {page + 1} of {Math.ceil(total / limit)}
            </span>
            <Button
              variant="outline"
              disabled={(page + 1) * limit >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ActivityLogPage;
